import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const dictionaryApi = createApi({
  reducerPath: "dictionary",
  tagTypes: ["dictionary"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getCities: builder.query<{ data: ApiTypes.Res.Dictionary[] }, { name?: string }>({
      query: arg => {
        const { name } = arg
        return {
          url: `/dictionary/cities`,
          method: "GET",
          params: {
            name,
          },
        }
      },
    }),
    getSuburbs: builder.query<{ data: ApiTypes.Res.Dictionary[] }, { name?: string; zip_code?: string }>({
      query: arg => {
        const { name, zip_code } = arg
        return {
          url: `/dictionary/suburbs`,
          method: "GET",
          params: {
            zip_code: (zip_code || undefined) as unknown as number,
            name,
          },
        }
      },
    }),
    getCountries: builder.query<{ data: ApiTypes.Res.Dictionary[] }, UITypes.Params>({
      query: arg => {
        return {
          url: `/dictionary/countries`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getZipCodes: builder.query<{ data: ApiTypes.Res.Dictionary[] }, UITypes.Params>({
      query: arg => {
        return {
          url: `/dictionary/zipCodes`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getStates: builder.query<{ data: ApiTypes.Res.Dictionary[] }, { name?: string; country_id?: number }>({
      query: arg => {
        const { name, country_id } = arg
        return {
          url: `/dictionary/states`,
          method: "GET",
          params: {
            name,
            country_id,
          },
        }
      },
    }),
    getResidentTypes: builder.query<{ data: ApiTypes.Res.Dictionary[] }, { name?: string }>({
      query: arg => {
        const { name } = arg
        return {
          url: `/dictionary/residentTypes`,
          method: "GET",
          params: {
            name,
          },
        }
      },
    }),
    getDeviceLocations: builder.query<{ data: ApiTypes.Res.Dictionary[] }, { name?: string; rt?: string }>({
      query: arg => {
        return {
          url: `/dictionary/deviceLocations`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getParentCompanies: builder.query<{ data: ApiTypes.Res.Dictionary[] }, { name?: string }>({
      query: arg => {
        const { name } = arg
        return {
          url: `/dictionary/parentCompanies`,
          method: "GET",
          params: {
            name,
          },
        }
      },
    }),
    getContractorAccountStatuses: builder.query<{ data: ApiTypes.Res.ContractorAccountStatus[] }, void>({
      query: () => {
        return {
          url: `/dictionary/contractorStatuses`,
          method: "GET",
        }
      },
    }),
    getPartnerCategories: builder.query<{ data: ApiTypes.Res.Dictionary[] }, void>({
      query: () => ({
        url: `/dictionary/partnerCategories`,
        method: "GET",
      }),
    }),
    getOrderTypes: builder.query<{ data: ApiTypes.Res.OrderType[] }, void>({
      query: () => ({
        url: `/dictionary/orders2/order-types`,
        method: "GET",
      }),
    }),
  }),
})

export const {
  useLazyGetCountriesQuery,
  useLazyGetCitiesQuery,
  useLazyGetSuburbsQuery,
  useLazyGetZipCodesQuery,
  useLazyGetStatesQuery,
  useLazyGetResidentTypesQuery,
  useLazyGetDeviceLocationsQuery,
  useLazyGetParentCompaniesQuery,
  useLazyGetContractorAccountStatusesQuery,
  useLazyGetPartnerCategoriesQuery,
  useGetOrderTypesQuery,
} = dictionaryApi
